import { getCurrentCountryKey, LOCALISATION_CONFIG } from "@/shared/config/localisation";
import type { InputOption } from "@/shared/types";
import { formatAmountByConfig } from "@/shared/utils/amountFormat";
import {
  Apple,
  BedDouble,
  Cigarette,
  CircleDot,
  Dumbbell,
  Leaf,
  MoonStar,
  Pill,
  Salad,
  ShieldCheck,
  Weight,
} from "lucide-react";
import { HealthMedicalConditionKey, HealthQuestionAnswer } from "../redux/type";

export const HEALTH_QUOTE_STEP_KEYS = {
  PERSONAL_DETAILS: "personalDetails",
  HEALTH_DETAILS: "healthDetails",
  HEALTH_HABITS: "healthHabits",
  QUICK_QUESTIONS: "quickQuestions",
  QUOTE_SELECTION: "quoteSelection",
  DOCUMENT_UPLOAD: "documentUpload",
  ADDITIONAL_INFO: "additionalInfo",
  PAYMENT: "payment",
} as const;

export type HealthQuoteStepKey =
  (typeof HEALTH_QUOTE_STEP_KEYS)[keyof typeof HEALTH_QUOTE_STEP_KEYS];

const formatCurrencyLabel = (amount: number) =>
  `${LOCALISATION_CONFIG.currencyCode} ${formatAmountByConfig(amount)}`;

export const HEALTH_COVERAGE_AMOUNT_OPTIONS = [
  { label: formatCurrencyLabel(500000), value: "500000" },
  { label: formatCurrencyLabel(1000000), value: "1000000" },
  { label: formatCurrencyLabel(1500000), value: "1500000" },
  { label: formatCurrencyLabel(2000000), value: "2000000" },
] as const;

const INDIA_CURRENT_INSURER_OPTIONS: InputOption[] = [
  { label: "Star Health", value: "star_health" },
  { label: "HDFC ERGO", value: "hdfc_ergo" },
  { label: "ICICI Lombard", value: "icici_lombard" },
  { label: "Niva Bupa", value: "niva_bupa" },
  { label: "Care Health", value: "care_health" },
  { label: "Aditya Birla Health", value: "aditya_birla" },
  { label: "ManipalCigna", value: "manipal_cigna" },
  { label: "Tata AIG", value: "tata_aig" },
  { label: "Bajaj Allianz", value: "bajaj_allianz" },
  { label: "Reliance Health", value: "reliance_health" },
];

const UAE_CURRENT_INSURER_OPTIONS: InputOption[] = [
  { label: "Daman", value: "daman" },
  { label: "Orient Insurance", value: "orient_insurance" },
  { label: "AXA Gulf", value: "axa_gulf" },
  { label: "Bupa Arabia", value: "bupa_arabia" },
  { label: "Oman Insurance", value: "oman_insurance" },
  { label: "Sukoon Insurance", value: "sukoon_insurance" },
  // { label: "MetLife UAE", value: "metlife_uae" },
  { label: "Allianz Care", value: "allianz_care" },
  { label: "Salama", value: "salama" },
  { label: "Abu Dhabi National Insurance (ADNIC)", value: "adnic" },
];

const CURRENT_INSURER_OPTIONS_BY_COUNTRY: Record<string, InputOption[]> = {
  UAE: UAE_CURRENT_INSURER_OPTIONS,
  IND: INDIA_CURRENT_INSURER_OPTIONS,
};

export const CURRENT_INSURER_OPTIONS =
  CURRENT_INSURER_OPTIONS_BY_COUNTRY[getCurrentCountryKey()] ?? UAE_CURRENT_INSURER_OPTIONS;

export const MAX_MEMBERS = 5;

export const RELATIONSHIP_OPTIONS = [
  { label: "Self", value: "self" },
  { label: "Spouse", value: "spouse" },
  { label: "Parent", value: "parent" },
  { label: "Son", value: "son" },
  { label: "Daughter", value: "daughter" },
  { label: "Mother", value: "mother" },
  { label: "Father", value: "father" },
];

export const TENURE_OPTIONS = Array.from({ length: 5 }, (_, index) => {
  const year = index + 1;

  return {
    label: `${year} Year${year === 1 ? "" : "s"}`,
    value: String(year),
  };
});

export const SUM_INSURED_OPTIONS = [
  { label: formatCurrencyLabel(500000), value: "500000" },
  { label: formatCurrencyLabel(1000000), value: "1000000" },
  { label: formatCurrencyLabel(1500000), value: "1500000" },
  { label: formatCurrencyLabel(2000000), value: "2000000" },
  { label: formatCurrencyLabel(5000000), value: "5000000" },
  { label: formatCurrencyLabel(10000000), value: "10000000" },
  { label: `${formatCurrencyLabel(20000000)} (2 Cr)`, value: "20000000" },
] as const;

export const HEALTH_POLICY_TYPE_OPTIONS: InputOption[] = [
  { label: "New Policy", value: "newPolicy" },
  { label: "Porting Policy", value: "porting_policy" },
];

export const HEALTH_COVERAGE_TYPE_OPTIONS: InputOption[] = [
  { label: "Individual", value: "individual" },
  { label: "Family", value: "family" },
  { label: "Family Floater", value: "family_floater" },
];

export const HEALTH_QUICK_QUESTION_HEIGHT_OPTIONS = Array.from({ length: 101 }, (_, index) => {
  const height = 120 + index;
  return { label: `${height} cm`, value: String(height) };
});

export const HEALTH_QUICK_QUESTION_WEIGHT_OPTIONS = Array.from({ length: 150 }, (_, index) => {
  const weight = index + 1;
  return { label: `${weight} kg`, value: String(weight) };
});

export const HEALTH_MEDICAL_CONDITION_OPTIONS: Array<{
  key: HealthMedicalConditionKey;
  label: string;
}> = [
  { key: "high_blood_pressure", label: "High Blood Pressure" },
  { key: "cholesterol", label: "Cholesterol" },
  { key: "diabetes", label: "Diabetes" },
  { key: "asthma_respiratory_issues", label: "Asthma / Respiratory Issues" },
  { key: "thyroid_disorder", label: "Thyroid Disorder" },
  { key: "heart_disease", label: "Heart Disease" },
  { key: "joint_pain_arthritis", label: "Joint Pain / Arthritis" },
  { key: "liver_disease", label: "Liver Disease" },
  { key: "kidney_disease", label: "Kidney Disease" },
  { key: "cancer", label: "Cancer" },
  { key: "mental_health_condition", label: "Mental Health Condition" },
  { key: "other", label: "Other (Please Specify)" },
];

export const HEALTH_QUESTION_LABELS = {
  medicalConditions: {
    title: "Medical Conditions",
    description: "Please select if you have any of the following.",
  },
  lifestyle: {
    title: "Lifestyle & Personal Details",
    description: "Your lifestyle information",
  },
  additionalInformation: {
    title: "Additional Information",
    description: "Any other information you would like to share?",
  },
} as const;

export const HEALTH_OCCUPATION_TYPE_OPTIONS: InputOption[] = [
  { label: "Salaried", value: "salaried" },
  { label: "Self Employed", value: "self_employed" },
  { label: "Business Owner", value: "business_owner" },
  { label: "Professional", value: "professional" },
  { label: "Government Employee", value: "government_employee" },
  { label: "Homemaker", value: "homemaker" },
  { label: "Student", value: "student" },
  { label: "Retired", value: "retired" },
  { label: "Farmer", value: "farmer" },
  { label: "Defense Personnel", value: "defense_personnel" },
  { label: "Driver / Transport", value: "driver_transport" },
  { label: "Factory Worker", value: "factory_worker" },
  { label: "Construction Worker", value: "construction_worker" },
  { label: "Others", value: "others" },
];

export const HEALTH_WORK_PROFILE_OPTIONS: InputOption[] = [
  { label: "Desk Based", value: "desk_based" },
  { label: "Field Based", value: "field_based" },
  { label: "Remote / Work From Home", value: "remote" },
  { label: "Manual / Physical Work", value: "manual" },
  { label: "Supervisory / Managerial", value: "managerial" },
  { label: "Professional Services", value: "professional" },
  { label: "Sales & Marketing", value: "sales_marketing" },
  { label: "Factory / Industrial", value: "industrial" },
  { label: "Healthcare Worker", value: "healthcare" },
  { label: "Transport & Logistics", value: "transport_logistics" },
  { label: "Construction & Engineering", value: "construction" },
  { label: "Defense / Security Services", value: "defense_security" },
  { label: "High Risk Occupation", value: "high_risk" },
  { label: "Others", value: "others" },
];
export const HEALTH_ANNUAL_INCOME_RANGE_OPTIONS: InputOption[] = [
  { label: `Below ${formatCurrencyLabel(50000)}`, value: "below_50000" },
  {
    label: `${formatCurrencyLabel(50000)} - ${formatCurrencyLabel(100000)}`,
    value: "50000_100000",
  },
  {
    label: `${formatCurrencyLabel(100000)} - ${formatCurrencyLabel(250000)}`,
    value: "100000_250000",
  },
  {
    label: `${formatCurrencyLabel(250000)} - ${formatCurrencyLabel(500000)}`,
    value: "250000_500000",
  },
  { label: `${formatCurrencyLabel(500000)}+`, value: "500000_plus" },
];

export const HEALTH_HABIT_OPTIONS = [
  { key: "non_smoker", label: "Non Smoker", icon: Cigarette },
  { key: "regular_exercise", label: "Regular Exercise", icon: Dumbbell },
  { key: "healthy_diet", label: "Healthy Diet", icon: Salad },
  { key: "occasional_alcohol", label: "Occasional Alcohol", icon: CircleDot },
  { key: "no_alcohol", label: "No Alcohol", icon: ShieldCheck },
  { key: "stress_management", label: "Stress Management", icon: MoonStar },
  { key: "adequate_sleep", label: "Adequate Sleep", icon: BedDouble },
  { key: "no_recreational_drugs", label: "No Recreational Drugs", icon: Pill },
  { key: "maintains_healthy_weight", label: "Maintains Healthy Weight", icon: Weight },
  { key: "regular_health_checkup", label: "Regular Health Checkup", icon: ShieldCheck },
  { key: "vegetarian_vegan", label: "Vegetarian / Vegan", icon: Leaf },
  { key: "other", label: "Other", icon: Apple },
] satisfies any[];

export const HEALTH_QUESTIONS = [
  {
    key: "preExistingMedicalConditions",
    question: "Do you have any pre-existing medical conditions?",
  },
  { key: "hospitalizedLast2Years", question: "Have you been hospitalized in the last 2 years?" },
  { key: "currentlyOnMedication", question: "Are you currently on any medication?" },
  { key: "surgeryLast5Years", question: "Have you undergone any surgery in the last 5 years?" },
  { key: "allergies", question: "Do you have any allergies?" },
  { key: "tobaccoOrNicotine", question: "Do you consume tobacco or any nicotine product?" },
  {
    key: "familyHistoryMajorIllness",
    question: "Do you have any family history of major illness?",
  },
  {
    key: "otherMedicalCondition",
    question: "Is there any other medical condition we should know?",
  },
] as const;

export const YES_NO_OPTIONS: Array<{ label: string; value: HealthQuestionAnswer }> = [
  { label: "Yes", value: "yes" },
  { label: "No", value: "no" },
];
